<template>
	<div v-if="mounted" class="application-career profile-view">
		<JonInfo>
			<template #message>
				Durch die Eingabe deines anonymen Lebenslaufs, kannst du deine Chancen erhöhen!<br/>
				<b>Gib zumindest die letzten 6 Jahre deines Lebens ein - Beruf, Ausbildung,
					sonstige Lebensstationen.</b>
			</template>
		</JonInfo>
		<div v-if="showGaps" class="alert-simple alert-simple-error mt-3">
			<div>Bitte gib zumindest die letzten 6 Jahre ein, um gefunden zu werden!</div>
			<div>Folgende Zeiträume fehlen: {{ gapsAsList }}</div>
		</div>
		<Group
			title="Berufserfahrung & Weitere/Sonstige Lebensstationen"
			:meta="meta"
			:has-any-career-item="careers.length > 0"
			:items="getItemsByGroupType('job-and-simple')"
			@removeItem="careerToRemove = $event"
			@saveItem="save"
		/>
		<Group
			title="Ausbildung"
			:meta="meta"
			:items="getItemsByGroupType('education')"
			education
			:section="{
				id: sectionEducation,
				finished: isEducationFinished
			}"
			further-title-items="Hast du eine weitere Ausbildung / Zusatzausbildung?"
			further-title-no-items="Hast du eine Ausbildung / Zusatzausbildung?"
			@removeItem="careerToRemove = $event"
			@saveItem="save"
			@finished="educationFinished"
		/>
		<ConfirmPopUp
			v-if="careerToRemove"
			title="Erfahrung löschen"
			@confirm="remove(careerToRemove)"
			@hidden="careerToRemove = undefined"
		>
			Möchtest du die Erfahrung <b>{{ careerToRemove.title }}</b> wirklich löschen?
		</ConfirmPopUp>
	</div>
</template>

<script>
import ConfirmPopUp from "@/vue/common/popup/ConfirmPopUp";
import _ from "lodash";
import {isDefined} from "@/helper/Value";
import {DateTime} from "luxon";
import * as CharacteristicTag from "@/helper/characteristic/Tag";
import {populate} from "@/helper/application/career/Populate";
import Group from "./career/Group"
import {mapState} from "vuex";
import {EDUCATION} from "@/helper/application/Section";
import JonInfo from "@/vue/common/JonInfo";
import {
	getDateComparisonValue,
	getMonthYearLabel
} from "@/helper/Date";
import * as Event from "@/helper/tracking/Event";
import EventData from "@/helper/tracking/EventData";

const GROUP_TYPE_JOB_AND_SIMPLE = 'job-and-simple';
const GROUP_TYPE_EDUCATION = 'education';

const EMPTY_ADD_CAREER_DATA = {
	start: undefined,
	end: undefined,
	careerType: undefined,
};

export default {

	components: {
		Group,
		ConfirmPopUp,
		JonInfo
	},

	props: {
		value: {
			type: Object,
			required: false,
		},
		sectionCompletions: {
			type: Array,
			required: true,
		},
		vitaInfo: {
			type: Object,
			required: true
		}
	},

	mounted ()
	{
		let now = DateTime.now();
		now = now.set({day: 1});

		this.startMonthYear = now.minus({year: 6});

		if (isDefined(this.value))
		{
			this.careers = populate(this.value.careers, this.meta);
		}

		this.addCareer = this.careers.length === 0;

		this.mounted = true;
	},

	data ()
	{
		let now = DateTime.now();
		now = now.set({day: 1});

		return {
			mounted: false,
			careerToRemove: undefined,
			careers: [],
			startMonthYear: undefined,
			endMonthYear: now,
			addCareer: true,
			addCareerData: EMPTY_ADD_CAREER_DATA,
		};
	},

	computed: {
		...mapState('meta', ['meta']),
		sectionEducation ()
		{
			return EDUCATION;
		},
		isEducationFinished ()
		{
			return isDefined(
				_.find(
					this.sectionCompletions,
					completion => completion.section === this.sectionEducation && !completion.skipped
				)
			);
		},
		hasCareers ()
		{
			return this.careers.length > 0;
		},
		groupedItems ()
		{
			let grouped = {};

			_.forEach(this.careers, career =>
			{
				const groupType = this.getGroupType(career);

				if (!grouped[groupType])
				{
					grouped[groupType] = {
						groupType,
						items: [],
					};
				}

				grouped[groupType].items.push(career);
			});

			return grouped;
		},
		showGaps ()
		{
			return this.vitaInfo.gaps.length > 0;
		},
		gapsAsList ()
		{
			return _.map(this.vitaInfo.gaps, gap =>
			{
				const from = DateTime.fromISO(gap.from);
				const to = DateTime.fromISO(gap.to);

				if (getDateComparisonValue(from) === getDateComparisonValue(to))
				{
					return getMonthYearLabel(from);
				}

				return getMonthYearLabel(from) + ' - ' + getMonthYearLabel(to);
			}).join(', ');
		}
	},

	methods: {
		getItemsByGroupType (groupType)
		{
			return this.groupedItems[groupType]?.items || [];
		},
		getGroupType (career)
		{
			return CharacteristicTag.hasTag(career.careerType, CharacteristicTag.CAREER_EDUCATION)
				? GROUP_TYPE_EDUCATION
				: GROUP_TYPE_JOB_AND_SIMPLE;
		},
		educationFinished ()
		{
			if (!this.isEducationFinished)
			{
				this.sectionCompletions.push({
					section: this.sectionEducation,
					skipped: false
				});
			}

			this.emitChangeEvent(true);
		},
		save (career)
		{
			Event.push(
				new EventData()
					.setEvent(Event.APPLICANT_VITA)
					.setAction('Speichern')
					.setName(career.title || career.careerType?.label || career.schoolType?.label)
			);

			const index = _.findIndex(this.careers, c => c.id === career.id);

			if (index >= 0)
			{
				this.$set(this.careers, index, career);
			}
			else
			{
				this.careers.push(career);
			}

			this.addCareer = false;
			this.addCareerData = {};

			this.emitChangeEvent(true);
		},
		remove (career)
		{
			Event.push(
				new EventData()
					.setEvent(Event.APPLICANT_VITA)
					.setAction('Eintrag löschen')
					.setName(career.title || career.careerType?.label)
			);

			this.careers = _.filter(this.careers, c => c.id !== career.id);

			this.careerToRemove = undefined;

			this.addCareer = !this.hasCareers;

			this.emitChangeEvent(true);
		},
		emitChangeEvent (fireChangeEvent)
		{
			const data = {
				careers: this.careers,
				educationFinished: this.isEducationFinished
			};

			this.$emit('input', data);

			if (fireChangeEvent)
			{
				this.$emit('change', data);
			}
		},
	}
}
</script>
